import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion'
import './typewriter.css'

type TypewriterRevealProps = {
  text: string
  enabled?: boolean
  speedMsPerChar?: number
  maxAnimatedChars?: number
  onDone?: () => void
  className?: string
  ariaLabel?: string
}

const DEFAULT_SPEED = 16
const DEFAULT_MAX_CHARS = 1000
const MIN_DELAY = 240
const MAX_DELAY = 450
const SPEED_SLOWDOWN = 1.15
const SCREENPRINT_MULTIPLIER = 4

const TypewriterReveal = ({
  text,
  enabled = true,
  speedMsPerChar = DEFAULT_SPEED,
  maxAnimatedChars = DEFAULT_MAX_CHARS,
  onDone,
  className = '',
  ariaLabel,
}: TypewriterRevealProps) => {
  const normalizedText = typeof text === 'string' ? text : ''
  const prefersReducedMotion = usePrefersReducedMotion()
  const totalLength = normalizedText.length
  const [visibleChars, setVisibleChars] = useState(() => (enabled ? 0 : totalLength))
  const [isComplete, setIsComplete] = useState(!enabled)
  const delayRef = useRef<number | null>(null)
  const rafRef = useRef<number | null>(null)
  const startTimeRef = useRef<number | null>(null)
  const doneRef = useRef(false)

  const animateLimit = useMemo(() => {
    return Math.min(totalLength, Math.max(0, maxAnimatedChars))
  }, [totalLength, maxAnimatedChars])

  const shouldAnimate = enabled && !prefersReducedMotion && animateLimit > 0 && totalLength > 0

  const clearTimers = useCallback(() => {
    if (delayRef.current) {
      window.clearTimeout(delayRef.current)
      delayRef.current = null
    }
    if (rafRef.current) {
      window.cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }
  }, [])

  const finalize = useCallback(() => {
    clearTimers()
    setVisibleChars(totalLength)
    setIsComplete(true)
    if (!doneRef.current) {
      doneRef.current = true
      onDone?.()
    }
  }, [clearTimers, onDone, totalLength])

  useEffect(() => {
    doneRef.current = false
  }, [normalizedText])

  useEffect(() => {
    if (!shouldAnimate) {
      finalize()
      return clearTimers
    }

    setVisibleChars(0)
    setIsComplete(false)
    startTimeRef.current = null

    const baseSpeed = Math.max(10, speedMsPerChar)
    const startDelay = Math.min(
      MAX_DELAY,
      Math.max(MIN_DELAY, Math.round(baseSpeed * 14 * SPEED_SLOWDOWN)),
    )

    const step = (timestamp: number) => {
      if (startTimeRef.current === null) {
        startTimeRef.current = timestamp
      }
      const elapsed = timestamp - startTimeRef.current
      const charsPerMs = (SCREENPRINT_MULTIPLIER / (baseSpeed * SPEED_SLOWDOWN)) || 0.01
      const burstBase = animateLimit > 900 ? 10 : animateLimit > 450 ? 7 : 5
      const computed = Math.floor(elapsed * charsPerMs) + burstBase
      const nextValue = Math.min(animateLimit, computed)

      setVisibleChars((previous) => (nextValue > previous ? nextValue : previous))

      if (nextValue >= animateLimit) {
        finalize()
        return
      }

      rafRef.current = window.requestAnimationFrame(step)
    }

    delayRef.current = window.setTimeout(() => {
      startTimeRef.current = null
      rafRef.current = window.requestAnimationFrame(step)
    }, startDelay)

    return clearTimers
  }, [animateLimit, clearTimers, finalize, shouldAnimate, speedMsPerChar])

  const classes = useMemo(() => {
    const list = ['typewriter-reveal']
    if (className) list.push(className)
    list.push(isComplete ? 'is-complete' : shouldAnimate ? 'is-animating' : 'is-idle')
    return list.join(' ')
  }, [className, isComplete, shouldAnimate])

  const renderedText = shouldAnimate ? normalizedText.slice(0, Math.min(visibleChars, totalLength)) : normalizedText

  return (
    <span className={classes} aria-label={ariaLabel} aria-live={shouldAnimate ? 'polite' : undefined}>
      {renderedText}
    </span>
  )
}

export default TypewriterReveal
